/**
 * Transfer document — `/inventory/transfers/$transferId`.
 *
 * The lifecycle is monotonic and server-owned: a draft may be edited, shipped
 * or cancelled; an in-transit document may only be received or cancelled;
 * received and cancelled are immutable terminal facts. This screen offers
 * exactly the transitions the current status allows, each gated on its own
 * permission, each sending the round-tripped ETag as `If-Match` and a fresh
 * Idempotency-Key. A refused transition is explained with the state that
 * refused it rather than failing mute.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Ban, PackageCheck, Truck } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  toast,
} from "@erp/ui";

import { ConflictDialog, surfaceProblem } from "@/components/form";
import { formatDateTime } from "@/lib/format";
import { isPreconditionFailed, newIdempotencyKey } from "@/features/shared/ops-api";

import {
  cancelTransfer,
  receiveTransfer,
  shipTransfer,
  updateTransfer,
  type Transfer,
} from "../api";
import {
  asInsufficientStockProblem,
  asPeriodClosedProblem,
  isImmutableDocument,
  isInvalidTransition,
} from "../problems";
import { inventoryKeys, useTransferDetail, useWarehouseOptions } from "../queries";
import { QuantityText } from "../components/decimal-text";

const TRANSFER_BADGE_VARIANTS = {
  draft: "outline",
  in_transit: "warning",
  received: "success",
  cancelled: "secondary",
} as const;

type TransitionAction = (
  transferId: string,
  body: { occurred_at?: string } | undefined,
  ifMatch: string,
  idempotencyKey: string,
) => Promise<unknown>;

interface TransitionOptions {
  transferId: string | undefined;
  etag: string | null;
  /** `<input type="datetime-local">` value; empty means server business time. */
  occurredAt: string;
  action: TransitionAction;
  successTitle: string;
  onSettledError: (error: unknown) => void;
  onDone: () => void;
}

/**
 * One transition click = one logical action: a fresh Idempotency-Key and the
 * ETag captured by the last read, so a replay deduplicates and a stale
 * document 412s instead of overwriting someone else's transition.
 */
function useTransferTransition({
  transferId,
  etag,
  occurredAt,
  action,
  successTitle,
  onSettledError,
  onDone,
}: TransitionOptions) {
  return useMutation({
    mutationFn: () => {
      if (transferId === undefined || etag === null) {
        throw new Error("Transfer is not loaded");
      }
      const body =
        occurredAt === "" ? undefined : { occurred_at: new Date(occurredAt).toISOString() };
      return action(transferId, body, etag, newIdempotencyKey());
    },
    onSuccess: () => {
      toast({ variant: "success", title: successTitle });
      onDone();
    },
    onError: onSettledError,
  });
}

export interface TransferDetailPageProps {
  transferId?: string;
  canUpdate?: boolean;
  canShip?: boolean;
  canReceive?: boolean;
  canCancel?: boolean;
  canReadMovements?: boolean;
  onOpenLegs?: (transferId: string) => void;
  onBack?: () => void;
}

export function TransferDetailPage({
  transferId,
  canUpdate = false,
  canShip = false,
  canReceive = false,
  canCancel = false,
  canReadMovements = false,
  onOpenLegs,
  onBack,
}: TransferDetailPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const [occurredAt, setOccurredAt] = useState("");
  const [requestedAt, setRequestedAt] = useState("");
  const [transitionError, setTransitionError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);

  const detail = useTransferDetail(transferId);
  const transfer: Transfer | null = detail.data?.data ?? null;
  const etag = detail.data?.etag ?? null;

  const warehouses = useWarehouseOptions().data ?? [];
  const warehouseCode = (id: string | undefined): string => {
    if (id === undefined) return "—";
    return warehouses.find((warehouse) => warehouse.id === id)?.code ?? id;
  };

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: inventoryKeys.transfers.all });
    void queryClient.invalidateQueries({ queryKey: inventoryKeys.stock.all });
    void queryClient.invalidateQueries({ queryKey: inventoryKeys.movements.all });
  };

  const explainFailure = (error: unknown): boolean => {
    if (isPreconditionFailed(error)) {
      setConflict(true);
      return true;
    }
    if (isInvalidTransition(error)) {
      setTransitionError(
        t(
          "inventory.transferDetail.invalidTransition",
          "This transfer is {{status}} — that action is no longer available. Reload to see the current state.",
          {
            status: t(
              `inventory.transferStatus.${transfer?.status ?? "draft"}`,
              transfer?.status ?? "",
            ),
          },
        ),
      );
      return true;
    }
    if (isImmutableDocument(error)) {
      setTransitionError(
        t(
          "inventory.transferDetail.immutable",
          "This transfer is already a posted fact and cannot be changed.",
        ),
      );
      return true;
    }
    const shortage = asInsufficientStockProblem(error);
    if (shortage !== null) {
      setTransitionError(
        shortage.detail ??
          t(
            "inventory.transferDetail.insufficientStock",
            "The source warehouse no longer holds enough stock to ship this transfer.",
          ),
      );
      return true;
    }
    const period = asPeriodClosedProblem(error);
    if (period !== null) {
      setTransitionError(
        t(
          "inventory.problems.periodClosedBody",
          "Period {{period}} is {{state}}. A movement has to be dated inside an open period.",
          {
            period: period.period,
            state: t(`inventory.periodState.${period.period_state}`, period.period_state),
          },
        ),
      );
      return true;
    }
    return false;
  };

  const transitionOptions = {
    transferId,
    etag,
    occurredAt,
    onSettledError: (error: unknown) => {
      if (explainFailure(error)) return;
      surfaceProblem(error, t);
    },
    onDone: () => {
      setTransitionError(null);
      invalidate();
    },
  };

  const shipMutation = useTransferTransition({
    ...transitionOptions,
    action: shipTransfer,
    successTitle: t("inventory.transferDetail.shipped", "Transfer shipped"),
  });
  const receiveMutation = useTransferTransition({
    ...transitionOptions,
    action: receiveTransfer,
    successTitle: t("inventory.transferDetail.received", "Transfer received"),
  });
  const cancelMutation = useTransferTransition({
    ...transitionOptions,
    action: cancelTransfer,
    successTitle: t("inventory.transferDetail.cancelled", "Transfer cancelled"),
  });

  const updateMutation = useMutation({
    mutationFn: () => {
      if (transferId === undefined || etag === null) throw new Error("Transfer is not loaded");
      return updateTransfer(
        transferId,
        { requested_at: new Date(requestedAt).toISOString() },
        etag,
      );
    },
    onSuccess: () => {
      setTransitionError(null);
      setRequestedAt("");
      toast({ variant: "success", title: t("inventory.transferDetail.saved", "Transfer updated") });
      invalidate();
    },
    onError: (error) => {
      if (explainFailure(error)) return;
      surfaceProblem(error, t);
    },
  });

  if (detail.isError) {
    return (
      <div className="p-4">
        <p role="alert" className="text-sm text-destructive">
          {t("inventory.transferDetail.loadError", "This transfer could not be loaded.")}
        </p>
      </div>
    );
  }

  if (transfer === null) {
    return (
      <div className="p-4">
        <p className="text-sm text-muted-foreground">{t("inventory.common.loading", "Loading…")}</p>
      </div>
    );
  }

  const isDraft = transfer.status === "draft";
  const isInTransit = transfer.status === "in_transit";
  const isTerminal = transfer.status === "received" || transfer.status === "cancelled";

  return (
    <div className="flex max-w-5xl flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex flex-wrap items-center gap-2 text-xl font-semibold">
            <span dir="ltr" className="font-mono text-base">
              {transfer.transfer_number}
            </span>
            <Badge variant={TRANSFER_BADGE_VARIANTS[transfer.status]}>
              {t(`inventory.transferStatus.${transfer.status}`, transfer.status)}
            </Badge>
          </h1>
          <p className="text-sm text-muted-foreground">
            {warehouseCode(transfer.source_warehouse_id)} →{" "}
            {warehouseCode(transfer.destination_warehouse_id)}
          </p>
        </div>
        {onBack !== undefined ? (
          <Button type="button" variant="ghost" onClick={onBack}>
            {t("inventory.transferDetail.back", "All transfers")}
          </Button>
        ) : null}
      </div>

      {isTerminal ? (
        <p className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
          {t(
            "inventory.transferDetail.terminal",
            "This document is a settled fact. Corrections are new movements, never edits.",
          )}
        </p>
      ) : null}

      {transitionError !== null ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm"
        >
          {transitionError}
        </p>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("inventory.transferDetail.lifecycle", "Lifecycle")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-muted-foreground">
                {t("inventory.transfers.columns.requested", "Requested")}
              </dt>
              <dd>{formatDateTime(transfer.requested_at, i18n.language)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">
                {t("inventory.transfers.columns.shipped", "Shipped")}
              </dt>
              <dd>
                {transfer.shipped_at === undefined
                  ? "—"
                  : formatDateTime(transfer.shipped_at, i18n.language)}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">
                {t("inventory.transfers.columns.received", "Received")}
              </dt>
              <dd>
                {transfer.received_at === undefined
                  ? "—"
                  : formatDateTime(transfer.received_at, i18n.language)}
              </dd>
            </div>
            {transfer.transit_warehouse_id !== undefined ? (
              <div>
                <dt className="text-muted-foreground">
                  {t("inventory.transferEntry.transitWarehouse", "Transit warehouse")}
                </dt>
                <dd>{warehouseCode(transfer.transit_warehouse_id)}</dd>
              </div>
            ) : null}
          </dl>

          {!isTerminal ? (
            <div className="flex flex-col gap-3 border-t pt-4">
              <div className="flex max-w-xs flex-col gap-1.5">
                <Label htmlFor="transfer-transition-at">
                  {t("inventory.transferDetail.transitionAt", "Post at (optional)")}
                </Label>
                <Input
                  id="transfer-transition-at"
                  type="datetime-local"
                  value={occurredAt}
                  onChange={(event) => setOccurredAt(event.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  {t(
                    "inventory.transferDetail.transitionAtHint",
                    "Leave empty to use the server's transaction time.",
                  )}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                {isDraft && canShip ? (
                  <Button
                    type="button"
                    loading={shipMutation.isPending}
                    disabled={etag === null || shipMutation.isPending}
                    onClick={() => shipMutation.mutate()}
                  >
                    <Truck className="size-4" aria-hidden="true" />
                    {t("inventory.transferDetail.ship", "Ship")}
                  </Button>
                ) : null}
                {isInTransit && canReceive ? (
                  <Button
                    type="button"
                    loading={receiveMutation.isPending}
                    disabled={etag === null || receiveMutation.isPending}
                    onClick={() => receiveMutation.mutate()}
                  >
                    <PackageCheck className="size-4" aria-hidden="true" />
                    {t("inventory.transferDetail.receive", "Receive")}
                  </Button>
                ) : null}
                {canCancel ? (
                  <Button
                    type="button"
                    variant="outline"
                    loading={cancelMutation.isPending}
                    disabled={etag === null || cancelMutation.isPending}
                    onClick={() => cancelMutation.mutate()}
                  >
                    <Ban className="size-4" aria-hidden="true" />
                    {t("inventory.transferDetail.cancel", "Cancel transfer")}
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}

          {isDraft && canUpdate ? (
            <div className="flex flex-wrap items-end gap-3 border-t pt-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="transfer-requested-at">
                  {t("inventory.transferDetail.reschedule", "Reschedule request")}
                </Label>
                <Input
                  id="transfer-requested-at"
                  type="datetime-local"
                  value={requestedAt}
                  onChange={(event) => setRequestedAt(event.target.value)}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                loading={updateMutation.isPending}
                disabled={requestedAt === "" || etag === null || updateMutation.isPending}
                onClick={() => updateMutation.mutate()}
              >
                {t("inventory.transferDetail.save", "Save draft")}
              </Button>
              <p className="w-full text-xs text-muted-foreground">
                {t(
                  "inventory.transferDetail.draftOnly",
                  "Only a draft can be edited; shipping freezes the document.",
                )}
              </p>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3">
          <CardTitle>
            <h2>{t("inventory.transferEntry.lines", "Lines")}</h2>
          </CardTitle>
          {canReadMovements && onOpenLegs !== undefined && transfer.status !== "draft" ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenLegs(transfer.id)}
            >
              {t("inventory.transferEntry.viewLegs", "View both legs")}
            </Button>
          ) : null}
        </CardHeader>
        <CardContent>
          <ul className="flex flex-col divide-y">
            {transfer.lines.map((line) => (
              <li key={line.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                <span className="w-8 text-muted-foreground">{line.line_number}</span>
                <span dir="ltr" className="min-w-0 flex-1 font-mono text-xs">
                  {line.variant_id}
                </span>
                <QuantityText value={line.qty} className="text-end" />
                {line.movement_ids !== undefined && line.movement_ids.length > 0 ? (
                  <Badge variant="secondary">
                    {t("inventory.transferDetail.legCount", "{{count}} legs", {
                      count: line.movement_ids.length,
                    })}
                  </Badge>
                ) : null}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <ConflictDialog
        open={conflict}
        onReload={() => {
          setConflict(false);
          void detail.refetch();
        }}
        onDismiss={() => setConflict(false)}
      />
    </div>
  );
}
